// Types for the Visual OutputMapper Editor
// Based on backend models in outsmart_backend/integrations/output_mapper/models.py

export type FieldType = 'string' | 'integer' | 'decimal' | 'boolean' | 'date' | 'datetime' | 'array' | 'object';

export type ConditionOperator =
    | 'equal'
    | 'not_equal'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'contains'
    | 'not_contains'
    | 'starts_with'
    | 'ends_with'
    | 'is_null'
    | 'is_not_null'
    | 'in'
    | 'not_in'
    | 'matches';

export type TransformFunction =
    | 'to_upper'
    | 'to_lower'
    | 'trim'
    | 'concat'
    | 'split'
    | 'join'
    | 'replace'
    | 'substring'
    | 'pad_left'
    | 'pad_right'
    | 'template'
    | 'format_date'
    | 'parse_date'
    | 'to_integer'
    | 'to_decimal'
    | 'to_string'
    | 'number_format'
    | 'normalize_decimal'
    | 'round'
    | 'math'
    | 'default'
    | 'coalesce'
    | 'lookup'
    | 'wrap_object'
    | 'unwrap_object';

export type ActionType =
    | 'extract_source'
    | 'assign_literal'
    | 'transform'
    | 'function_call'
    | 'conditional'
    | 'map_array';

export interface FieldDefinition {
    name: string;
    type: FieldType | string;
    description?: string;
    is_array?: boolean;
    item_type?: string;
    object_type?: string;
    nested_fields?: FieldDefinition[];
    is_custom?: boolean;
}

export interface TypeDefinition {
    name: string;
    description?: string;
    fields: FieldDefinition[];
}

export interface Condition {
    operator: ConditionOperator;
    value?: any;
    value_type?: FieldType;
}

export interface CompoundCondition {
    logic: 'and' | 'or';
    conditions: (Condition | CompoundCondition)[];
}

export interface ExtractSourceAction {
    action_type: 'extract_source';
    source: string;
}

export interface AssignLiteralAction {
    action_type: 'assign_literal';
    value: any;
}

export interface TransformAction {
    action_type: 'transform';
    transformation_function: TransformFunction;
    source?: string;
    function_params?: Record<string, any>;
}

export interface FunctionCallAction {
    action_type: 'function_call';
    function_name: string;
    arguments?: Record<string, any>;
}

export interface ConditionalAction {
    action_type: 'conditional';
    source: string;
    condition: Condition | CompoundCondition;
    true_actions: Action[];
    false_actions?: Action[];
}

export interface FieldMapping {
    target: string;
    description?: string;
    actions: Action[];
    is_required?: boolean;
    default_value?: any;
}

export interface MapArrayAction {
    action_type: 'map_array';
    source: string;
    item_alias?: string;
    index_alias?: string;
    field_mappings: FieldMapping[];
}

export type Action =
    | ExtractSourceAction
    | AssignLiteralAction
    | TransformAction
    | FunctionCallAction
    | ConditionalAction
    | MapArrayAction;

export type OutputType = 'json' | 'csv' | 'xml' | 'txt' | 'xlsx' | 'template';

/** Text format of a `template` output: XML is escaped and XSD-validated, TXT/CSV are written as-is. */
export type TemplateFormat = 'xml' | 'txt' | 'csv';

/** Line breaks and character encoding of a text output file (every output type except xlsx). */
export type LineEnding = 'lf' | 'crlf';
export type FileEncoding = 'utf-8' | 'utf-8-sig' | 'cp1252' | 'iso-8859-1' | 'iso-8859-15';
export type IterationType = 'record' | 'line_items' | 'article_suggestions';
export type MappingSource = 'channel_fields' | 'integration_default';

export interface OutputMapperConfig {
    name: string;
    version?: string;
    description?: string;
    /** @deprecated superseded by `iteration_type`; kept for reading legacy stored configs. */
    output_level?: 'document' | 'article';
    output_type?: OutputType;
    iteration_type?: IterationType;
    default_mapping_source?: MappingSource;
    /** Importable serializer module selected when output_type is `xml` (orders only). */
    xml_serializer_module?: string;
    /** Field delimiter used when output_type is `txt`; defaults to "," on the backend. */
    txt_delimiter?: string;
    /** Sandboxed Jinja template rendered over the mapper result when output_type is `template`. */
    template?: string | null;
    /** Text format of the template output; absent means `xml`. Decides the delivered file type. */
    template_format?: TemplateFormat;
    /** Jinja template of the output file name (any output type); empty keeps `<record id>.<extension>`. */
    file_name_template?: string | null;
    /** Line breaks of the output file (all but xlsx); absent/null keeps them as generated. */
    line_ending?: LineEnding | null;
    /** Character encoding of the output file (all but xlsx); absent means `utf-8`. */
    encoding?: FileEncoding;
    /** Key of the backend XSD the template output is validated against; null/absent disables validation. */
    template_xsd_key?: string | null;
    /** Customer-specific XSD content used when template_xsd_key is `custom` (max 1 MB, self-contained). */
    template_xsd?: string | null;
    input_types: TypeDefinition[];
    action_types?: { name: string; label?: string; description?: string; parameters?: unknown[] }[];
    field_types?: { value: string; label: string }[];
    condition_operators?: { value: string; label: string }[];
    transform_functions?: { name: string; description?: string; label?: string; category?: string }[];
    /** Available XML serializer modes for the editor dropdown (orders only); absent when N/A. */
    xml_serializer_modes?: { value: string; label: string; description?: string }[];
    /** Starter templates for the Template tab's insert-default dropdown; injected by the backend at read time. */
    template_defaults?: {
        key: string;
        label: string;
        template: string;
        xsd_key?: string | null;
        template_format?: TemplateFormat;
        /** `standard`: a standard output format; `generic`: a skeleton for a custom format. */
        group?: 'standard' | 'generic';
        /** Values of the starter that no target field of this config carries yet. */
        unbound?: string[];
    }[];
    /** Available XSDs for the template validation dropdown; injected by the backend at read time. */
    template_xsds?: { key: string; label: string }[];
    /** Target fields the owner accepts (e.g. an ERP API), for the add-field dropdowns; injected at read time. */
    target_field_options?: TargetFieldOption[];
    output_mapping: FieldMapping[];
    glossary?: Record<string, string>;
    custom_functions?: string[];
}

export interface TargetFieldOption {
    target: string;
    description?: string;
    /** The array mapping whose items carry this field; null for a top-level field. */
    array_target?: string | null;
}

export interface AliasContextEntry {
    alias: string;
    typeName?: string;
}

export interface ActionTypeInfo {
    value: ActionType;
    label: string;
    description: string;
}

type ParamType = 'string' | 'number' | 'boolean' | 'array' | 'object';

export interface TransformFunctionInfo {
    name: TransformFunction;
    label: string;
    description: string;
    category: 'string' | 'date' | 'number' | 'logic';
    params: { name: string; type: ParamType; required?: boolean; description?: string }[];
}

// Helper to define params compactly: [name, type, required?, description?]
type ParamTuple = [string, ParamType, boolean?, string?];
const p = (defs: ParamTuple[]): TransformFunctionInfo['params'] =>
    defs.map(([name, type, required, description]) => ({
        name,
        type,
        ...(required && { required }),
        ...(description && { description }),
    }));

const tf = (
    name: TransformFunction,
    label: string,
    description: string,
    category: TransformFunctionInfo['category'],
    params: ParamTuple[] = []
): TransformFunctionInfo => ({ name, label, description, category, params: p(params) });

export const TRANSFORM_FUNCTIONS: TransformFunctionInfo[] = [
    tf('to_upper', 'To Upper', 'Convert to uppercase', 'string'),
    tf('to_lower', 'To Lower', 'Convert to lowercase', 'string'),
    tf('trim', 'Trim', 'Remove leading/trailing whitespace', 'string'),
    tf('concat', 'Concat', 'Concatenate strings', 'string', [['values', 'array', true, 'Values to concatenate']]),
    tf('split', 'Split', 'Split string into array', 'string', [['separator', 'string', true, 'Separator character']]),
    tf('join', 'Join', 'Join array into string', 'string', [['separator', 'string', false, 'Separator character']]),
    tf('replace', 'Replace', 'Replace substring', 'string', [
        ['pattern', 'string', true, 'Pattern to match'],
        ['replacement', 'string', true, 'Replacement text'],
    ]),
    tf('substring', 'Substring', 'Extract substring', 'string', [
        ['start', 'number', true, 'Start index'],
        ['end', 'number', false, 'End index'],
    ]),
    tf('pad_left', 'Pad Left', 'Pad string on left', 'string', [
        ['length', 'number', true, 'Target length'],
        ['char', 'string', false, 'Padding character'],
    ]),
    tf('pad_right', 'Pad Right', 'Pad string on right', 'string', [
        ['length', 'number', true, 'Target length'],
        ['char', 'string', false, 'Padding character'],
    ]),
    tf('template', 'Template', 'Apply template string', 'string', [
        ['template', 'string', true, 'Template with {placeholders}'],
    ]),
    tf('format_date', 'Format Date', 'Format date to string', 'date', [['format', 'string', true, 'strftime format']]),
    tf('parse_date', 'Parse Date', 'Parse string to date', 'date', [['format', 'string', false, 'strftime format']]),
    tf('to_integer', 'To Integer', 'Convert to integer', 'number'),
    tf('to_decimal', 'To Decimal', 'Convert to decimal', 'number'),
    tf('to_string', 'To String', 'Convert to string', 'number'),
    tf('number_format', 'Number Format', 'Format number', 'number', [
        ['decimal_places', 'number', false, 'Decimal places'],
        ['decimal_separator', 'string', false, 'Decimal separator'],
        ['thousands_separator', 'string', false, 'Thousands separator'],
    ]),
    tf('normalize_decimal', 'Normalize Decimal', 'Normalize decimal string', 'number'),
    tf('round', 'Round', 'Round number', 'number', [['precision', 'number', false, 'Decimal places']]),
    tf('math', 'Math', 'Mathematical operation', 'number', [
        ['operation', 'string', true, 'add/subtract/multiply/divide'],
        ['operand', 'number', true, 'Second operand'],
    ]),
    tf('default', 'Default', 'Provide default value if null', 'logic', [['value', 'string', true, 'Default value']]),
    tf('coalesce', 'Coalesce', 'Return first non-null value', 'logic', [['values', 'array', true, 'Values to check']]),
    tf('lookup', 'Lookup', 'Lookup value in mapping table', 'logic', [
        ['mapping', 'object', true, 'Key-value mapping'],
        ['default', 'string', false, 'Default if not found'],
    ]),
    tf('wrap_object', 'Wrap Object', 'Wrap value in object', 'logic', [['key', 'string', true, 'Object key']]),
    tf('unwrap_object', 'Unwrap Object', 'Extract value from object', 'logic', [['key', 'string', true, 'Object key']]),
];

export const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
    { value: 'equal', label: 'Equals' },
    { value: 'not_equal', label: 'Not equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'is_null', label: 'Is null' },
    { value: 'is_not_null', label: 'Is not null' },
    { value: 'in', label: 'In list' },
    { value: 'not_in', label: 'Not in list' },
    { value: 'matches', label: 'Matches regex' },
];

export const FIELD_TYPES: { value: FieldType; label: string }[] = [
    { value: 'string', label: 'String' },
    { value: 'integer', label: 'Integer' },
    { value: 'decimal', label: 'Decimal' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'DateTime' },
    { value: 'array', label: 'Array' },
    { value: 'object', label: 'Object' },
];
